import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Info, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MonthWeeksList } from "@/components/month/MonthWeeksList";
import { MonthSubmitDialog } from "@/components/month/MonthSubmitDialog";
import { fmtHrs, formatDate } from "@/lib/timesheet";

const TOTALS = [["standard", "Standard"], ["overtime", "Overtime"], ["leave", "Leave"]];

const MonthTotals = ({ totals }) => (
  <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
    {TOTALS.map(([k, l]) => (
      <Card key={k} className="p-4" data-testid={`month-summary-${k}`}>
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
        <div className="mt-1.5 font-heading text-xl font-extrabold tabular">{fmtHrs(totals[k])}</div>
      </Card>
    ))}
    <Card className="border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 p-4" data-testid="month-summary-total_worked">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--accent))]">Total worked</div>
      <div className="mt-1.5 font-heading text-xl font-extrabold tabular">{fmtHrs(totals.total_worked)}</div>
    </Card>
  </div>
);

export default function MonthEnd() {
  const { ym } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState(null);
  const [invoice, setInvoice] = useState("");

  useEffect(() => {
    setView(null);
    api.get(`/months/${ym}`).then(({ data }) => { setView(data); setInvoice(data.submission?.invoice_number || data.suggested_invoice || ""); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  }, [ym]);

  const submit = async (declarationName) => {
    try {
      const { data } = await api.post(`/months/${ym}/submit`, { invoice_number: invoice, declaration_name: declarationName, declared: true });
      setView(data);
      toast.success(`${data.label} submitted to Accounts`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!view) return <Layout><div className="flex justify-center py-24 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div></Layout>;
  const sub = view.submission;

  return (
    <Layout>
      <button onClick={() => navigate("/")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="flex flex-wrap items-center gap-3">
        <h1 data-testid="month-title" className="font-heading text-3xl font-extrabold tracking-tight">{view.label}</h1>
        <span data-testid="month-status-badge" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${sub ? "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300" : "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300"}`}>
          {sub ? <><Lock className="h-3 w-3" /> Submitted</> : "Open"}
        </span>
      </div>
      <p className="mt-1 text-muted-foreground">Your month-end submission. Hours are counted by day, so weeks that span two months are split.</p>

      {sub && (
        <div data-testid="month-submitted-banner" className="mt-4 flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
          <Lock className="h-4 w-4" /> Submitted {formatDate(sub.submitted_at)} by {sub.declaration_name}. Days in this month are now locked.
        </div>
      )}

      <Card className="mt-6 p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2"><Label>Name</Label><Input value={user?.name || ""} disabled className="bg-muted/50" data-testid="month-name-input" /></div>
          <div className="space-y-2"><Label htmlFor="month-pick">Month</Label><Input id="month-pick" data-testid="month-picker" type="month" value={ym} onChange={(e) => e.target.value && navigate(`/month/${e.target.value}`)} className="font-mono" /></div>
          <div className="space-y-2"><Label>Role / team</Label><Input value={view.position || ""} disabled className="bg-muted/50" data-testid="month-role-input" /></div>
          <div className="space-y-2"><Label htmlFor="month-inv">Invoice number <span className="text-rose-600">*</span></Label><Input id="month-inv" data-testid="month-invoice-input" value={invoice} disabled={!!sub} onChange={(e) => setInvoice(e.target.value)} placeholder="e.g. INV-0001" />{!sub && view.suggested_invoice && invoice === view.suggested_invoice && <p data-testid="month-invoice-suggested" className="text-xs text-muted-foreground">Filled in from last month - change it if needed.</p>}</div>
        </div>
      </Card>

      <MonthTotals totals={view.totals} />
      <MonthWeeksList weeks={view.weeks} />

      {!sub && (
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          {view.unsubmitted_weeks.length > 0 ? (
            <span data-testid="month-blocking-msg" className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
              <Info className="h-4 w-4" /> Submit week {view.unsubmitted_weeks.join(", ")} first.
            </span>
          ) : view.awaiting_approval_weeks.length > 0 ? (
            <span data-testid="month-awaiting-msg" className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
              <Info className="h-4 w-4" /> Please wait until your last weekly timesheet is approved before submitting your monthly invoice.
            </span>
          ) : !invoice.trim() && (
            <span data-testid="month-invoice-msg" className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
              <Info className="h-4 w-4" /> Enter your invoice number to submit.
            </span>
          )}
          <MonthSubmitDialog label={view.label} defaultName={user?.name} disabled={!view.can_submit || !invoice.trim()} onConfirm={submit} />
        </div>
      )}
    </Layout>
  );
}
